import type { AppProps } from 'next/app'
import '../styles/main.css'
import '../styles/globals.scss'
import '../styles/Backoffice.css'
import 'bootstrap-daterangepicker/daterangepicker.css'
import CustomScript from '../components/CustomScript'
import '../public/js/loader.js'
import { persistor, store } from '@/store'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { ReactElement, ReactNode, useEffect } from 'react'
import { NextPage } from 'next'
import { CircularStd } from '@/utils/fonts'
import { useRouter } from 'next/router'
import ObservabilityInit from '@/components/Observability'
import { eventPublisher } from '@/utils/event-publisher'
import { SessionProvider } from 'next-auth/react'

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
    getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout
}

export default function App({ Component, pageProps: { session, ...pageProps } }: AppPropsWithLayout) {
    const getLayout = Component.getLayout ?? (page => page)

    const router = useRouter()
    useEffect(() => {
        // Function to handle button clicks
        const handleButtonClick = (button: HTMLButtonElement) => {
            const description = button.getAttribute('data-description') ?? ''

            if (button.classList.contains('tracked-button')) {
                eventPublisher.logAnalyticsEvent({
                    eventType: 'Regular',
                    actionName: 'click',
                    payload: {
                        buttonName: button.innerText,
                        buttonDescription: description
                    }
                })
            }
        }

        // Function to handle click events
        const handleClickEvent = (event: any) => {
            const target = event.target as HTMLElement
            const buttonIsTracked = target.classList.contains('tracked-button')

            if (target && target instanceof HTMLButtonElement && buttonIsTracked) {
                handleButtonClick(target)
            }
        }

        document.addEventListener('click', handleClickEvent)

        return () => {
            document.removeEventListener('click', handleClickEvent)
        }
    }, [router.events])

    return (
        <SessionProvider session={session}>
            <div className={`${CircularStd.className} w-100 h-100`}>
                <ObservabilityInit />
                <Provider store={store}>
                    <PersistGate loading={null} persistor={persistor}>
                        {getLayout(<Component {...pageProps} />)}
                        <CustomScript />
                    </PersistGate>
                </Provider>
            </div>
        </SessionProvider>
    )
}
